import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { ChevronRight, ChevronLeft, CheckCircle, ArrowRight, FileText } from 'lucide-react';
import { quizQuestions } from '../data/quizQuestions';
import { submitQuiz } from '../lib/api';
import { trackQuizComplete, trackCTAClick } from '../lib/ga4';

const DiagnosticQuiz = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [selectedSector, setSelectedSector] = useState(null);

  const totalQuestions = quizQuestions.length;
  const progress = ((currentStep + 1) / totalQuestions) * 100;
  const currentQuestion = quizQuestions[currentStep];
  const isLastQuestion = currentStep === totalQuestions - 1;
  const hasAnswered = answers[currentQuestion?.id] !== undefined;

  // Determine level based on score - NEW RULES
  const getScoreLevel = (score) => {
    if (score >= 70) return { level: 'Avancé', color: 'green', bgColor: 'bg-green-100', textColor: 'text-green-800' };
    if (score >= 40) return { level: 'Intermédiaire', color: 'blue', bgColor: 'bg-blue-100', textColor: 'text-blue-800' };
    return { level: 'Débutant', color: 'orange', bgColor: 'bg-orange-100', textColor: 'text-orange-800' };
  };

  const handleSelectAnswer = (option) => {
    setAnswers({
      ...answers,
      [currentQuestion.id]: {
        answer: option.value,
        score_value: option.score,
      },
    });
    
    // Capture sector from question 2
    if (currentQuestion.id === 2) {
      setSelectedSector(option.value);
    }
  };

  const handleNext = () => {
    if (isLastQuestion) {
      handleSubmit();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Format answers for API
      const formattedAnswers = Object.entries(answers).map(([questionId, data]) => ({
        question_id: parseInt(questionId),
        answer: data.answer,
        score_value: data.score_value,
      }));

      // Get UTM params from URL
      const urlParams = new URLSearchParams(window.location.search);
      
      const quizData = {
        answers: formattedAnswers,
        utm_source: urlParams.get('utm_source'),
        utm_medium: urlParams.get('utm_medium'),
        utm_campaign: urlParams.get('utm_campaign'),
        page_url: window.location.href,
      };

      const response = await submitQuiz(quizData);
      setResult(response);
      
      // Calculate level with NEW rules
      const calculatedLevel = response.score >= 70 ? 'Avancé' : response.score >= 40 ? 'Intermédiaire' : 'Débutant';
      
      // Store score and level in localStorage for form
      localStorage.setItem('lead_score', response.score.toString());
      localStorage.setItem('lead_level', calculatedLevel);
      
      console.log('[Quiz] Score stored in localStorage:', response.score, calculatedLevel);
      
      // Track completion
      trackQuizComplete(response.score, response.qualified);
    } catch (err) {
      console.error('Quiz submission error:', err);
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRestart = () => {
    setCurrentStep(0);
    setAnswers({});
    setResult(null);
    setError(null);
    // Clear localStorage on restart
    localStorage.removeItem('lead_score');
    localStorage.removeItem('lead_level');
  };

  // Result screen
  if (result) {
    const scoreInfo = getScoreLevel(result.score);
    // Calculate level with consistent rules
    const calculatedLevel = result.score >= 70 ? 'Avancé' : result.score >= 40 ? 'Intermédiaire' : 'Débutant';
    
    const handleGetDiagnostic = () => {
      trackCTAClick('recevoir_diagnostic', 'quiz_result');
      
      // Store in localStorage (persistent)
      localStorage.setItem('lead_score', result.score.toString());
      localStorage.setItem('lead_level', calculatedLevel);
      
      // Store quiz data in sessionStorage for form pre-fill (includes sector)
      const quizDataObj = {
        score: result.score,
        level: calculatedLevel,
        sector: selectedSector || '',
      };
      sessionStorage.setItem('quizData', JSON.stringify(quizDataObj));
      
      console.log('[Quiz] Diagnostic requested, data stored:', quizDataObj);
      
      // Dispatch custom event to notify form
      window.dispatchEvent(new Event('quizDataSet'));
      
      // Scroll to qualifying form
      const formElement = document.getElementById('formulaire');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth' });
      }
    };

    return (
      <section
        id="diagnostic"
        className="py-8 md:py-16"
        style={{ backgroundColor: '#1C2E4A', scrollMarginTop: '80px' }}
        data-testid="diagnostic-result-section"
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-xl">
            {/* Success icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
            </div>

            {/* Score */}
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Votre score de maturité IA
              </h2>
              <div className={`text-6xl font-black mb-3`} style={{ color: '#1C2E4A' }}>
                {result.score}/100
              </div>
              <span className={`inline-block px-6 py-2 rounded-full text-base font-semibold ${scoreInfo.bgColor} ${scoreInfo.textColor}`}>
                Niveau : {scoreInfo.level}
              </span>
            </div>

            {/* Recommendation */}
            <div className="bg-gray-50 rounded-xl p-6 mb-8">
              <h3 className="font-semibold text-gray-900 mb-2">Notre recommandation</h3>
              <p className="text-gray-600">{result.recommendation}</p>
            </div>

            {/* CTAs */}
            <div className="flex flex-col gap-4">
              <Button
                onClick={handleGetDiagnostic}
                className="w-full text-white rounded-full py-4 text-lg font-semibold transition-all duration-300 hover:-translate-y-0.5"
                style={{ backgroundColor: '#1C2E4A' }}
                data-testid="quiz-result-cta-diagnostic"
              >
                <FileText className="mr-2 w-5 h-5" />
                Recevoir mon diagnostic personnalisé
              </Button>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="#formulaire"
                  className="flex-1"
                  onClick={() => trackCTAClick('formulaire_from_quiz', 'quiz_result')}
                  data-testid="quiz-result-cta-form"
                >
                  <Button 
                    variant="outline"
                    className="w-full rounded-full py-4"
                  >
                    Demander un accompagnement
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </a>
                <Button
                  variant="ghost"
                  onClick={handleRestart}
                  className="flex-1 rounded-full py-4 text-gray-600"
                  data-testid="quiz-restart-btn"
                >
                  Refaire le diagnostic
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Quiz screen
  return (
    <section
      id="diagnostic"
      className="py-8 md:py-16"
      style={{ backgroundColor: '#1C2E4A', scrollMarginTop: '80px' }}
      data-testid="diagnostic-quiz-section"
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-6 md:mb-8">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white mb-3 md:mb-4">
            Diagnostic gratuit
          </h2>
          <p className="text-blue-100 text-base md:text-lg px-4">
            Évaluez le potentiel IA de votre entreprise en 2 minutes
          </p>
        </div>

        {/* Quiz Card */}
        <div className="bg-white rounded-2xl p-6 md:p-8 shadow-xl">
          {/* Progress */}
          <div className="mb-8">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>Question {currentStep + 1} sur {totalQuestions}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-2" data-testid="quiz-progress" />
          </div>

          {/* Question */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-6" data-testid="quiz-question">
              {currentQuestion.question}
            </h3>

            {/* Options */}
            <div className="space-y-3" role="radiogroup" aria-label={currentQuestion.question}>
              {currentQuestion.options.map((option, index) => {
                const isSelected = answers[currentQuestion.id]?.answer === option.value;
                
                return (
                  <button
                    key={index}
                    onClick={() => handleSelectAnswer(option)}
                    className={`w-full p-4 text-left rounded-xl border-2 transition-all ${
                      isSelected
                        ? 'border-blue-700 bg-blue-50 ring-2 ring-blue-700'
                        : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                    }`}
                    role="radio"
                    aria-checked={isSelected}
                    data-testid={`quiz-option-${index}`}
                  >
                    <span className={`font-medium ${isSelected ? 'text-blue-700' : 'text-gray-900'}`}>
                      {option.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-lg" role="alert" aria-live="polite">
              {error}
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between gap-4">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentStep === 0}
              className="rounded-full"
              data-testid="quiz-prev-btn"
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              Précédent
            </Button>

            <Button
              onClick={handleNext}
              disabled={!hasAnswered || isSubmitting}
              className="bg-[#1C2E4A] hover:bg-[#0F1E33] text-white rounded-full"
              data-testid="quiz-next-btn"
            >
              {isSubmitting ? (
                'Calcul en cours...'
              ) : isLastQuestion ? (
                <>
                  Voir mon score
                  <CheckCircle className="w-5 h-5 ml-2" />
                </>
              ) : (
                <>
                  Suivant
                  <ChevronRight className="w-5 h-5 ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DiagnosticQuiz;
